import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { startProCheckout } from '../lib/checkout';
import { track } from '../lib/analytics';
import { PASSIVE_PLACEMENT_BY_PATH, placementOn } from '../lib/exitIntent';
import { X, Copy, Check } from 'lucide-react';

const SS_SHOWN = 'cmx_exit_shown';
const SS_DEADLINE = 'cmx_exit_deadline';
const SS_SPOTS = 'cmx_exit_spots';
const STAFF_NEVER = ['admin', 'teacher'];

/**
 * Exit-intent conversion popup — mounted once in App.js.
 * Passive placements (home · pricing · dashboard) trigger on: mouse leaving
 * through the top of the viewport, 30 s of inactivity, or a sharp scroll-up
 * after reading (phones). Moment placements (test finished · test left ·
 * course finished) fire through `triggerExitIntent()`. Every placement has
 * an on/off switch in Admin → Promotions; visitors see it once per session,
 * free students every time (or every `exit_repeat_hours`). Pro and staff never.
 */
export default function ExitIntentModal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [spots, setSpots] = useState(null);
  const [copied, setCopied] = useState(false);
  const shownOnPathRef = useRef('');

  const isPro = !!(user && user.subscription_status && user.subscription_status !== 'free');
  const isStaff = !!user && STAFF_NEVER.includes(user.role);
  const audienceOk = !isPro && !isStaff;

  useEffect(() => {
    api.get('/api/promo/config').then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const enabled = !!cfg && cfg.exit_enabled !== false && audienceOk;
  const passivePlacement = PASSIVE_PLACEMENT_BY_PATH[location.pathname] || '';
  // Visitors only ever see the public placements; the dashboard needs a logged-in free student.
  const passiveOk = enabled && !!passivePlacement && placementOn(cfg, passivePlacement)
    && (user ? true : passivePlacement !== 'dashboard');

  const mayShow = useCallback(() => {
    if (!user) return !sessionStorage.getItem(SS_SHOWN);
    const hours = Number(cfg?.exit_repeat_hours || 0);
    if (hours <= 0) return true;
    const last = Number(localStorage.getItem(`cmx_exit_last_${user.id}`) || 0);
    return Date.now() - last > hours * 3600000;
  }, [user, cfg]);

  const show = useCallback((where) => {
    if (!mayShow()) return;
    if (!user) sessionStorage.setItem(SS_SHOWN, '1');
    else localStorage.setItem(`cmx_exit_last_${user.id}`, String(Date.now()));
    shownOnPathRef.current = window.location.pathname;
    setPlacement(where);
    setOpen(true);
    track('exit_intent_shown', { location: where });
  }, [mayShow, user]);

  // Moment triggers (test finished / left midway / course finished)
  useEffect(() => {
    if (!enabled || !user) return undefined;
    let timer = null;
    const onMoment = (e) => {
      const where = e.detail?.placement;
      if (!where || !placementOn(cfg, where)) return;
      clearTimeout(timer);
      timer = setTimeout(() => show(where), Number(e.detail?.delayMs || 0));
    };
    window.addEventListener('cmx:exit-intent', onMoment);
    return () => { clearTimeout(timer); window.removeEventListener('cmx:exit-intent', onMoment); };
  }, [enabled, user, cfg, show]);

  // Passive triggers: exit through top, 30s idle, sharp scroll-up — once per page visit (a new visit to the same page may show it again)
  useEffect(() => {
    if (shownOnPathRef.current && shownOnPathRef.current !== location.pathname) shownOnPathRef.current = '';
  }, [location.pathname]);
  useEffect(() => {
    if (!passiveOk) return undefined;
    const path = location.pathname;
    const fire = () => { if (shownOnPathRef.current !== path) show(passivePlacement); };
    const onMouseOut = (e) => { if (!e.relatedTarget && e.clientY <= 0) fire(); };
    let idleTimer = null;
    const resetIdle = () => { clearTimeout(idleTimer); idleTimer = setTimeout(fire, 30000); };
    let lastY = window.scrollY;
    let maxY = 0;
    const onScroll = () => {
      const y = window.scrollY;
      maxY = Math.max(maxY, y);
      if (maxY > 600 && lastY - y > 240) fire();
      lastY = y;
      resetIdle();
    };
    document.addEventListener('mouseout', onMouseOut);
    window.addEventListener('scroll', onScroll, { passive: true });
    const idleEvents = ['mousemove', 'keydown', 'touchstart', 'click'];
    idleEvents.forEach((ev) => window.addEventListener(ev, resetIdle, { passive: true }));
    resetIdle();
    return () => {
      clearTimeout(idleTimer);
      document.removeEventListener('mouseout', onMouseOut);
      window.removeEventListener('scroll', onScroll);
      idleEvents.forEach((ev) => window.removeEventListener(ev, resetIdle));
    };
  }, [passiveOk, passivePlacement, location.pathname, show]);

  // Countdown — deadline persisted per session so it doesn't reset
  useEffect(() => {
    if (!open || !cfg) return undefined;
    let deadline = Number(sessionStorage.getItem(SS_DEADLINE) || 0);
    if (!deadline || deadline < Date.now()) {
      deadline = Date.now() + Number(cfg.exit_timer_minutes || 10) * 60000;
      sessionStorage.setItem(SS_DEADLINE, String(deadline));
    }
    const tick = () => setSecondsLeft(Math.max(0, Math.floor((deadline - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [open, cfg]);

  // "Only N spots left" drops by one while the visitor reads the modal
  useEffect(() => {
    if (!open || !cfg) return undefined;
    const start = Number(sessionStorage.getItem(SS_SPOTS) || cfg.exit_spots || 5);
    setSpots(start);
    const id = setTimeout(() => {
      const next = Math.max(1, start - 1);
      setSpots(next);
      sessionStorage.setItem(SS_SPOTS, String(next));
    }, 9000);
    return () => clearTimeout(id);
  }, [open, cfg]);

  if (!open || !cfg || !enabled) return null;

  const pct = Number(cfg.exit_discount_pct || 50);
  const price = Number(cfg.price || 149);
  const symbol = cfg.currency_symbol || '$';
  const discounted = (price * (1 - pct / 100)).toFixed(2);
  const code = cfg.exit_coupon_code || 'CELPIP50';
  const mm = String(Math.floor((secondsLeft ?? 0) / 60)).padStart(2, '0');
  const ss = String((secondsLeft ?? 0) % 60).padStart(2, '0');
  const ctaLabel = cfg.exit_cta_label || `Yes. Give Me ${pct}% Off`;
  const priceSuffix = cfg.exit_price_suffix || 'one-time';
  const priceNote = cfg.exit_price_note || '3 months of full access — no auto-renewal';
  const valueText = cfg.exit_value_text || `${symbol}${discounted} one-time for 3 full months — cheaper than failing once.`;

  const copyCode = () => {
    try {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* clipboard unavailable */ }
  };

  const claim = async () => {
    try { navigator.clipboard.writeText(code); } catch { /* clipboard unavailable */ }
    track('exit_intent_claim', { code, pct, location: placement });
    if (user) {
      try {
        const url = await startProCheckout(price, code);
        if (url) return;
      } catch { /* checkout unavailable */ }
      setOpen(false);
      navigate('/pricing');
    } else {
      setOpen(false);
      navigate('/signup?intent=pro');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 130 }} onClick={() => setOpen(false)} data-testid="exit-intent-modal" data-placement={placement}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg relative max-h-[94vh] overflow-y-auto p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => setOpen(false)} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center text-neutral-500 hover:bg-neutral-200 transition" data-testid="exit-intent-close">
          <X size={16} />
        </button>

        <h2 className="text-2xl sm:text-3xl font-extrabold text-neutral-900 text-center mb-1" data-testid="exit-title">
          <span role="img" aria-label="wait">✋</span> {cfg.exit_title || 'Wait…'}
        </h2>
        <p className="text-neutral-500 text-center text-sm sm:text-base font-medium mb-5" data-testid="exit-subtitle">{cfg.exit_subtitle || 'Thinking about the price?'}</p>

        {/* Countdown */}
        <div className="rounded-2xl bg-slate-950 text-center py-3.5 mb-4">
          <p className="text-white text-[11px] font-bold tracking-widest uppercase mb-0.5">{pct}% discount ends in</p>
          <p className="text-red-500 font-mono text-4xl font-extrabold tabular-nums tracking-wider" data-testid="exit-timer">{mm}:{ss}</p>
        </div>

        {/* Price */}
        <div className="rounded-2xl border border-neutral-200 p-4 text-center mb-5">
          <div className="flex items-center justify-center gap-2 mb-0.5">
            <span className="text-lg font-bold text-rose-400 line-through">{symbol}{price} {priceSuffix}</span>
            <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-500 text-xs font-bold">-{pct}%</span>
          </div>
          <div className="text-3xl sm:text-4xl font-extrabold text-neutral-900" data-testid="exit-price-discounted">{symbol}{discounted}<span className="text-lg font-bold"> {priceSuffix}</span></div>
          <div className="flex items-center justify-center gap-2 mt-1 text-xs text-neutral-500 flex-wrap">
            <span data-testid="exit-price-note">{priceNote}</span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-500 font-semibold" data-testid="exit-spots-left">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />Only {spots ?? cfg.exit_spots} spots left
            </span>
          </div>
        </div>

        {/* Coupon */}
        <p className="text-[11px] font-bold tracking-widest uppercase text-neutral-400 text-center mb-2">Use code at checkout</p>
        <div className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border-2 border-dashed border-blue-300 bg-blue-50/40 mb-4" data-testid="exit-coupon-code">
          <span className="font-mono font-extrabold text-lg tracking-widest text-blue-700" data-testid="exit-coupon-code-text">{code}</span>
          <button onClick={copyCode} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-blue-100 transition" data-testid="exit-copy-code-btn">
            {copied ? <Check size={15} className="text-emerald-600" /> : <Copy size={15} className="text-blue-400" />}
            <span className="text-xs font-semibold text-blue-500">{copied ? 'Copied!' : 'Copy'}</span>
          </button>
        </div>

        {/* CTA */}
        <button onClick={claim}
          className="w-full py-4 rounded-xl bg-blue-600 text-white font-bold text-base sm:text-lg hover:bg-blue-700 transition shadow-lg shadow-blue-600/25 mb-5"
          data-testid="exit-claim-btn">
          {ctaLabel}
        </button>

        {/* Persuasion stack */}
        <div className="space-y-2.5">
          <div className="rounded-xl bg-rose-50 border border-rose-100 px-4 py-3 text-[13px] leading-relaxed text-rose-800 whitespace-pre-line" data-testid="exit-fear-box">
            <span role="img" aria-label="alert">🚨</span> <strong>{cfg.exit_fear_text || 'Most students who study alone fail about 4 times.\nThey waste over $800 and waste nearly 3 months.'}</strong>
          </div>
          <div className="rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3 text-[13px] leading-relaxed text-emerald-800" data-testid="exit-smart-box">
            <span role="img" aria-label="check">✅</span> <strong>{cfg.exit_smart_text || 'Do it the smart way. Pay less. Pass on your next attempt.'}</strong>
          </div>
          <div className="rounded-xl bg-blue-50 border border-blue-100 px-4 py-3 text-[13px] leading-relaxed text-blue-800" data-testid="exit-value-box">
            <span role="img" aria-label="idea">💡</span> <strong>{valueText}</strong>
          </div>
        </div>
      </div>
    </div>
  );
}
